import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Megaphone, PiggyBank, ShieldCheck, Clock3, MessageSquare, ArrowRight, Quote } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Landing() {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [tops, setTops] = useState([]);
  const [stats, setStats] = useState({ sold_this_month: 142, members: 1240 });
  useEffect(() => {
    api.get("/listings", { params: { limit: 10 } })
      .then(({ data }) => setFeatured(data))
      .catch(() => setFeatured([]));
    api.get("/sellers/top", { params: { limit: 6 } })
      .then(({ data }) => setTops(data))
      .catch(() => setTops([]));
    api.get("/stats")
      .then(({ data }) => setStats((s) => ({
        sold_this_month: Math.max(data.sold_this_month, 142),
        members: Math.max(data.members, 1240),
      })))
      .catch(() => {});
  }, []);

  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative">
          {/* Background video / fallback image */}
          <div className="absolute inset-0 -z-10">
            <video
              autoPlay muted loop playsInline
              poster="/hero-banner.jpg"
              data-testid="hero-video"
              className="absolute inset-0 w-full h-full object-cover object-center"
            >
              <source src="/hero-video.mp4" type="video/mp4" />
            </video>
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/70 to-transparent lg:from-secondary/95 lg:via-secondary/50 lg:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 min-h-[640px] flex items-center">
            <div className="max-w-2xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground drop-shadow-sm" data-testid="hero-headline">
                {t("landing.hero_title_1")}{" "}
                <span className="italic text-primary">{t("landing.hero_title_2")}</span>.
              </h1>

              <p
                data-testid="hero-slogan"
                className="mt-6 font-display italic text-2xl sm:text-3xl text-foreground/90 max-w-xl leading-snug"
              >
                {t("landing.hero_slogan_prefix")}{" "}
                <span className="text-primary not-italic font-semibold">{t("landing.hero_slogan_pink")}</span>
              </p>

              <span
                className="mt-6 inline-flex items-center gap-2 px-3 h-8 rounded-full border border-primary/50 bg-background/90 backdrop-blur text-primary text-sm font-medium"
                data-testid="beta-badge"
              >
                {t("landing.beta_pill")}
              </span>

              <p className="mt-4 text-lg">
                <strong className="font-semibold">{t("landing.hero_sub_bold")}</strong>{" "}
                <span className="text-primary font-semibold">{t("landing.hero_sub_pink")}</span>
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/browse" data-testid="hero-browse-cta">
                  <Button size="lg" className="rounded-full h-12 px-7 gap-2 shadow-lg shadow-primary/30">
                    {t("landing.cta_browse")} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a
                  href="#beta"
                  data-testid="hero-beta-cta"
                  className="text-primary font-medium hover:underline underline-offset-4"
                >
                  {t("landing.cta_beta")}
                </a>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 max-w-lg">
                <TrustBadge icon={<ShieldCheck className="w-4 h-4" />} text={t("landing.trust_stripe")} />
                <TrustBadge icon={<Clock3 className="w-4 h-4" />} text={t("landing.trust_paid")} />
                <TrustBadge icon={<MessageSquare className="w-4 h-4" />} text={t("landing.trust_msg")} />
              </div>

              {/* Social proof strip (viral) */}
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground/70" data-testid="social-proof">
                <div className="flex items-center gap-2">
                  <span className="font-display text-2xl text-primary font-semibold">{stats.sold_this_month}</span>
                  <span>{t("social_proof.sold")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-2xl text-primary font-semibold">{stats.members.toLocaleString("fr-BE")}</span>
                  <span>{t("social_proof.members")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-2xl text-primary font-semibold">0 %</span>
                  <span>{t("social_proof.commission")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marie testimonial */}
      <Testimonial />

      {/* Top sellers leaderboard */}
      {tops.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="top-sellers-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="overline text-primary mb-2">{t("top_sellers.kicker")}</div>
              <h2 className="font-display text-3xl sm:text-4xl">{t("top_sellers.title")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {tops.map((s, i) => (
              <div key={s.seller_id}
                data-testid={`top-seller-${i}`}
                className="rounded-2xl bg-card border border-border p-4 text-center hover:shadow-md hover:shadow-primary/5 transition-shadow"
              >
                <div className="relative mx-auto w-20 h-20 rounded-full overflow-hidden bg-accent">
                  {s.sample_image
                    ? <img src={s.sample_image} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center font-display text-2xl text-primary">
                        {(s.seller_name || "?").slice(0, 1)}
                      </div>}
                  {i < 3 && (
                    <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow">
                      #{i + 1}
                    </span>
                  )}
                </div>
                <div className="mt-3 font-medium text-sm truncate">{s.seller_name}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {s.listings_count} {t("top_sellers.items")}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}


      {/* 3 steps */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{t("landing.steps_title")}</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          <Step n={t("landing.step1_n")} title={t("landing.step1_title")} desc={t("landing.step1_desc")}
            icon={<Camera className="w-5 h-5" />} />
          <Step n={t("landing.step2_n")} title={t("landing.step2_title")} desc={t("landing.step2_desc")}
            icon={<Megaphone className="w-5 h-5" />} />
          <Step n={t("landing.step3_n")} title={t("landing.step3_title")} desc={t("landing.step3_desc")}
            icon={<PiggyBank className="w-5 h-5" />} />
        </div>
      </section>

      {/* Mobile-first tagline */}
      <section className="bg-secondary/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t("landing.mobile_title_1")}{" "}
              <span className="text-primary">{t("landing.mobile_title_2")}</span>.
            </h2>
            <p className="mt-5 text-muted-foreground text-lg max-w-md">
              {t("landing.mobile_sub")}
            </p>
            <Link to="/browse" className="inline-block mt-8">
              <Button variant="outline" size="lg" className="rounded-full h-12 px-6 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground">
                {t("landing.mobile_cta")}
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=400&q=80",
            ].map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[3/4] bg-muted">
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-primary mb-2">{t("landing.featured_kicker")}</div>
              <h2 className="font-display text-3xl sm:text-4xl">{t("landing.featured_title")}</h2>
            </div>
            <Link to="/browse" className="text-primary font-medium hover:underline underline-offset-4 hidden sm:inline-block" data-testid="featured-see-all">
              {t("landing.featured_all")}
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 stagger" data-testid="featured-grid">
            {featured.slice(0, 10).map((it) => <ProductCard key={it.id} item={it} />)}
          </div>
        </section>
      )}

      <BetaForm />
    </div>
  );
}

function Testimonial() {
  const { t } = useTranslation();
  const OTHERS = [
    { k: "lucie", initial: "L" },
    { k: "chantal", initial: "C" },
    { k: "carla", initial: "C" },
    { k: "sophie", initial: "S" },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="testimonial-section">
      {/* Marie — featured */}
      <div className="relative rounded-3xl border border-primary/20 bg-gradient-to-br from-secondary/60 to-background p-8 sm:p-14 overflow-hidden">
        <div className="absolute -top-8 -left-4 text-primary/15 pointer-events-none">
          <Quote className="w-32 h-32" strokeWidth={1} />
        </div>
        <div className="relative">
          <div className="overline text-primary">{t("testimonial.kicker")}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t("testimonial.title_1")}{" "}
            <span className="italic text-primary">{t("testimonial.title_2")}</span>
          </h2>
          <blockquote className="mt-6 text-lg sm:text-xl leading-relaxed text-foreground/85 max-w-3xl">
            {t("testimonial.quote")}
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center font-display text-primary text-lg" aria-hidden>
              M
            </div>
            <div className="text-sm">
              <div className="font-semibold" data-testid="testimonial-author">{t("testimonial.author")}</div>
            </div>
          </div>
          <a
            href="#beta"
            data-testid="testimonial-cta"
            className="mt-8 inline-flex items-center gap-2 text-primary font-medium hover:underline underline-offset-4"
          >
            {t("testimonial.cta")}
          </a>
        </div>
      </div>

      {/* Others — grid of 4 */}
      <div className="mt-16 text-center">
        <div className="overline text-primary">{t("testimonial.others_kicker")}</div>
        <h3 className="mt-3 font-display text-3xl sm:text-4xl leading-tight" data-testid="others-title">
          {t("testimonial.others_title")}
        </h3>
      </div>

      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="others-grid">
        {OTHERS.map(({ k, initial }) => (
          <div
            key={k}
            data-testid={`testimonial-${k}`}
            className="rounded-2xl border border-border bg-card p-6 flex flex-col hover:shadow-md hover:shadow-primary/5 hover:border-primary/40 transition-all"
          >
            <div className="flex items-center gap-1 text-primary" aria-label="5 étoiles">
              {"★★★★★".split("").map((s, i) => (<span key={i} className="text-base">{s}</span>))}
            </div>
            <blockquote className="mt-4 text-[15px] leading-relaxed text-foreground/85 flex-1">
              {t(`testimonial.${k}_quote`)}
            </blockquote>
            <div className="mt-6 pt-4 border-t border-border flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center font-display text-primary text-base">
                {initial}
              </div>
              <div className="text-sm leading-tight">
                <div className="font-semibold">{t(`testimonial.${k}_name`)}</div>
                <div className="text-xs text-muted-foreground">
                  {t(`testimonial.${k}_city`)} · {t(`testimonial.${k}_age`)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PhoneMockup() {
  return (
    <div className="relative w-[280px] sm:w-[320px] mx-auto">
      <div className="relative rounded-[3rem] bg-neutral-900 p-3 shadow-2xl shadow-primary/20">
        <div className="rounded-[2.4rem] overflow-hidden bg-secondary aspect-[9/19.5] relative">
          <img
            src="https://images.pexels.com/photos/23876287/pexels-photo-23876287.jpeg?auto=compress&cs=tinysrgb&w=940"
            alt="VintedStyle app"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-neutral-900" />
        </div>
      </div>
    </div>
  );
}

function TrustBadge({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className="text-primary">{icon}</span>
      {text}
    </div>
  );
}

function Step({ n, title, desc, icon }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 text-left hover:shadow-md hover:shadow-primary/5 transition-shadow">
      <div className="w-11 h-11 rounded-2xl bg-accent flex items-center justify-center text-primary">
        {icon}
      </div>
      <div className="mt-4 text-primary text-xs font-semibold uppercase tracking-widest">{n}</div>
      <div className="mt-1 font-semibold text-lg">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

function BetaForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "" });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t("landing.beta_error"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/beta/join", form);
      toast.success(t("landing.beta_success"));
      setForm({ name: "", email: "", phone: "", city: "" });
    } catch {
      toast.error(t("landing.beta_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="beta" className="bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-3xl border border-border bg-secondary/40 p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="overline text-primary">{t("landing.beta_kicker")}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t("landing.beta_title_1")}{" "}
            <span className="text-primary">{t("landing.beta_title_2")}</span>.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg">
            {t("landing.beta_desc_1")} <strong>{t("landing.beta_desc_bold")}</strong>{" "}
            {t("landing.beta_desc_2")}{" "}
            <span className="text-foreground font-semibold">{t("landing.beta_desc_bold2")}</span>.
          </p>

          <form onSubmit={onSubmit} className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="beta-form">
            <Input placeholder={t("landing.beta_name")} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="beta-name" className="h-12 rounded-full bg-background" />
            <Input type="email" placeholder={t("landing.beta_email")} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="beta-email" className="h-12 rounded-full bg-background" />
            <Input type="tel" placeholder={t("landing.beta_phone")} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="beta-phone" className="h-12 rounded-full bg-background" />
            <Input placeholder={t("landing.beta_city")} value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="beta-city" className="h-12 rounded-full bg-background" />
            <div className="sm:col-span-2">
              <Button type="submit" size="lg" disabled={busy}
                data-testid="beta-submit"
                className="w-full sm:w-auto rounded-full h-12 px-8 shadow-lg shadow-primary/25">
                {busy ? t("landing.beta_sending") : t("landing.beta_submit")}
              </Button>
            </div>
          </form>
          <p className="mt-5 text-xs text-muted-foreground">{t("landing.beta_privacy")}</p>
        </div>
      </div>
    </section>
  );
}
